import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchClonablePackages, fetchPackageWithItems } from '@/lib/api';
import type { ClonablePackage } from '@/lib/api';
import type { GiftPackage } from '@/lib/types';

interface Props {
  /** Het pakket dat nu in de cockpit open staat (links). */
  currentPkg: GiftPackage;
  /** Huidige project uitsluiten uit de keuzelijst. */
  excludeProjectId: string;
}

interface ItemLine {
  key: string;
  name: string;
  supplier: string;
  qty: number;
  unitPrice: number;
}

function toLines(pkg: GiftPackage | null): ItemLine[] {
  if (!pkg?.items) return [];
  return pkg.items
    .filter((it) => it.gift_product)
    .map((it) => ({
      key: it.id,
      name: it.gift_product!.name,
      supplier: it.gift_product!.supplier?.name ?? it.gift_product!.brand ?? '—',
      qty: it.quantity_per_package,
      unitPrice: it.gift_product!.unit_purchase_price ?? 0,
    }));
}

function recipeTotal(lines: ItemLine[]): number {
  return lines.reduce((sum, l) => sum + l.qty * l.unitPrice, 0);
}

function PackageColumn({
  title,
  subtitle,
  lines,
}: {
  title: string;
  subtitle?: string;
  lines: ItemLine[];
}) {
  const total = recipeTotal(lines);
  return (
    <div className="flex-1 rounded-lg border border-bmade-200 bg-white dark:border-bmade-800 dark:bg-bmade-900/40">
      <div className="border-b border-bmade-200 px-3 py-2 dark:border-bmade-800">
        <h3 className="truncate text-sm font-semibold text-bmade-950 dark:text-white">{title}</h3>
        {subtitle && <p className="truncate text-[11px] text-bmade-500">{subtitle}</p>}
        <p className="mt-0.5 text-[11px] text-bmade-600 dark:text-bmade-400">
          {lines.length} {lines.length === 1 ? 'item' : 'items'} · EUR {total.toFixed(2)} / pakket
        </p>
      </div>
      {lines.length === 0 ? (
        <p className="px-3 py-3 text-[11px] text-bmade-400">Geen items.</p>
      ) : (
        <ul className="divide-y divide-bmade-100 dark:divide-bmade-800">
          {lines.map((l) => (
            <li key={l.key} className="flex items-start justify-between gap-2 px-3 py-1.5 text-xs">
              <div className="min-w-0">
                <div className="truncate font-medium text-bmade-900 dark:text-bmade-100">
                  {l.name}
                </div>
                <div className="truncate text-[10px] text-bmade-500">{l.supplier}</div>
              </div>
              <div className="shrink-0 text-right text-[11px] text-bmade-600 dark:text-bmade-400">
                {l.qty} × EUR {l.unitPrice.toFixed(2)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * ComparisonViewer — toont het huidige pakket naast een eerder pakket (read-only)
 * zodat Mark configuraties kan vergelijken zonder te klonen (S2, GD-2702).
 */
export function ComparisonViewer({ currentPkg, excludeProjectId }: Props) {
  const [options, setOptions] = useState<ClonablePackage[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState<string>('');
  const [otherPkg, setOtherPkg] = useState<GiftPackage | null>(null);
  const [loadingOther, setLoadingOther] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoadingList(true);
    fetchClonablePackages(excludeProjectId)
      .then((list) => {
        if (active) setOptions(list);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Pakketten ophalen mislukt');
      })
      .finally(() => {
        if (active) setLoadingList(false);
      });
    return () => {
      active = false;
    };
  }, [excludeProjectId]);

  useEffect(() => {
    if (!selectedId) {
      setOtherPkg(null);
      return;
    }
    let active = true;
    setLoadingOther(true);
    setError(null);
    fetchPackageWithItems(selectedId)
      .then((pkg) => {
        if (active) setOtherPkg(pkg);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Pakket laden mislukt');
      })
      .finally(() => {
        if (active) setLoadingOther(false);
      });
    return () => {
      active = false;
    };
  }, [selectedId]);

  const currentLines = useMemo(() => toLines(currentPkg), [currentPkg]);
  const otherLines = useMemo(() => toLines(otherPkg), [otherPkg]);

  const selected = options.find((o) => o.id === selectedId) ?? null;
  const otherTitle = selected
    ? `${selected.client_name ?? selected.project_name}`
    : 'Eerder pakket';
  const otherSubtitle = selected
    ? [selected.project_name, selected.variant_label].filter(Boolean).join(' · ')
    : undefined;

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-[11px] uppercase tracking-wider text-bmade-500">
          Vergelijk met een eerder pakket
        </label>
        {loadingList ? (
          <div className="flex items-center gap-2 text-xs text-bmade-500">
            <Loader2 className="h-3 w-3 animate-spin" /> Laden...
          </div>
        ) : options.length === 0 ? (
          <p className="text-xs text-bmade-500">Geen eerdere pakketten met items gevonden.</p>
        ) : (
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white"
          >
            <option value="">Kies een pakket...</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {(o.client_name ?? o.project_name) +
                  ' · ' +
                  o.project_name +
                  (o.variant_label ? ` · ${o.variant_label}` : '') +
                  ` (${o.item_count})`}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <PackageColumn
          title={currentPkg.variant_label ?? currentPkg.name}
          subtitle="Huidig pakket"
          lines={currentLines}
        />
        {loadingOther ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-bmade-200 py-6 text-xs text-bmade-500 dark:border-bmade-800">
            <Loader2 className="mr-2 h-3 w-3 animate-spin" /> Laden...
          </div>
        ) : otherPkg ? (
          <PackageColumn title={otherTitle} subtitle={otherSubtitle} lines={otherLines} />
        ) : (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-bmade-200 py-6 text-center text-xs text-bmade-400 dark:border-bmade-800">
            Kies links een eerder pakket om naast het huidige te tonen.
          </div>
        )}
      </div>
    </div>
  );
}
